import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { Citizenship } from "../../Dashboard/constants/citizenship";
import { employmentStatusOptions } from "../../Dashboard/constants/employment";
import { exchangeNameOptions, } from "../../Dashboard/constants/exchangeName";
import { genderOptions } from "../../Dashboard/constants/gender";
import { incomeRangeOptions, } from "../../Dashboard/constants/incomeRange";
import { investingObjectiveOptions, } from "../../Dashboard/constants/investingObjective";
import { investorTypeOptions, } from "../../Dashboard/constants/investorType";
import { martialStatusOptions, } from "../../Dashboard/constants/martialStatus";
import { relationshipOptions, } from "../../Dashboard/constants/relationship";
import { custodianOptions, DateSchema, Prefix, Suffix, taxTypeOptions, USAStates, } from "../../Types";
import ValidationError from "../../ZodError";
export const Name = z.object({
    first: z.string(),
    middle: z.string().nullable(),
    last: z.string(),
    prefix: Prefix.nullable(),
    suffix: Suffix.nullable(),
});
export const Address = z.object({
    street: z.string(),
    unit: z.string().nullable(),
    city: z.string(),
    state: z.enum(USAStates),
    postalCode: z.string(),
    country: z.string(),
});
export const TaxInformation = z.object({
    taxIdType: z.enum(taxTypeOptions),
    taxId: z.string(),
});
export const PersonalDetails = z.object({
    name: Name,
    gender: z.enum(genderOptions).nullable(),
    maritalStatus: z.enum(martialStatusOptions).nullable(),
    dateOfBirth: z.string().nullable(),
    citizenship: Citizenship,
    taxInformation: TaxInformation.nullable(),
    incomeRange: z.enum(incomeRangeOptions).nullable(),
    estimatedNetWorthMillions: z.number().nullable(),
    investorType: z.enum(investorTypeOptions).nullable(),
    investingObjective: z.enum(investingObjectiveOptions).nullable(),
    iraOutsideFarther: z.number().nullable(),
    joinedDate: DateSchema.nullable(),
});
export const ContactInformation = z.object({
    emailAddress: z.string(),
    phoneNumber: z.string().nullable(),
});
export const TaxBracketInfo = z.object({
    taxBracketLTG: z.number().int().nullable(),
    taxBracketOrdinary: z.number().int().nullable(),
    capitalLossCarryover: z.number().nullable(),
});
export const Employer = z.object({
    occupation: z.string(),
    startYear: z.number(),
    employerName: z.string().min(1).max(40),
    employerAddress: Address,
});
export const EmploymentStatus = z.enum(employmentStatusOptions);
export const ClientEmploymentDetails = z.object({
    employmentStatus: EmploymentStatus.nullable(),
    employer: Employer.nullable(),
});
const ClientInvestorProfile = z.object({
    personalDetails: PersonalDetails,
    contactInformation: ContactInformation,
    address: Address.nullable(),
    taxBracketInfo: TaxBracketInfo,
    // NOTE: for now employmentDetails are duplicated, it's located here and at the response top level
    employmentDetails: ClientEmploymentDetails,
});
const EmployeeRelative = z.object({
    name: Name,
    relationship: z.enum(relationshipOptions),
});
const BrokerDealerRelative = z.object({
    name: Name,
    company: z.string(),
    relationship: z.enum(relationshipOptions),
});
const ClientAffiliations = z.object({
    isUsOfficial: z.boolean().nullable(),
    isFartherEmployee: z.boolean(),
    exchangeName: z.enum(exchangeNameOptions).nullable(),
    publicCompanyName: z.string().nullable(),
    brokerDealerName: z.string().nullable(),
    fartherEmployeeRelative: EmployeeRelative.nullable(),
    brokerDealerRelative: BrokerDealerRelative.nullable(),
    custodianName: z.enum(custodianOptions).nullable(),
});
const ClientDashboardSettings = z.object({
    db_accounts: z.boolean(),
    db_documents: z.boolean(),
    db_checklist: z.boolean(),
    db_familyView: z.boolean(),
    db_customPortfolios: z.boolean(),
    db_fundingSettings: z.boolean(),
    db_transfers: z.boolean(),
    db_advisor: z.boolean(),
    db_messaging: z.boolean(),
    db_altAssets: z.boolean(),
    db_lendingAccounts: z.boolean(),
    db_insurance: z.boolean(),
    db_performanceGroups: z.boolean(),
    pg_flows: z.boolean(),
    pg_holdings: z.boolean(),
    pg_projections: z.boolean(),
    pg_summary: z.boolean(),
    pg_trades: z.boolean(),
});
const GetClientProfileResponse = z.object({
    investorProfile: ClientInvestorProfile,
    affiliations: ClientAffiliations,
    employmentDetails: ClientEmploymentDetails,
    isAccreditedInvestor: z.boolean().nullable(),
    hasSignedFartherAgreement: z.boolean(),
    dashboardSettings: ClientDashboardSettings,
});
export const url = (clientId) => environment() === "PROD"
    ? `https://middleware.prod.farther.com/entity/v1/clientside/${clientId}/profile`
    : `https://middleware.uat.farther.com/entity/v1/clientside/${clientId}/profile`;
const getClientProfile = async (clientId, config) => {
    const { data } = await axios.get(url(clientId), {
        headers: {
            Client: config.clientId,
            Authorization: `Bearer ${config.jwt}`,
            ...(config.advisorId && { Advisor: config.advisorId }),
        },
    });
    const decoded = GetClientProfileResponse.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = "getClientProfile returned unexpected data.";
        errorLog(errorMessage, decoded.error.message);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
};
export default getClientProfile;
//# sourceMappingURL=getClientProfile.js.map